// client/src/components/map/assetDisplay.ts
//
// Drawing a truck's `display` (ST-00276 phase 2, map colours & states) on every LexiStar surface.
// The server evaluates each organisation's display rules on the live state (status board rows
// carry `display`); Replay and the Trips view evaluate the rule version in force at each replayed
// moment with the same pure module (map/displayRules.ts, a byte-identical copy of the server's).
// This module turns a display into what the surfaces already draw:
//   * visualOfDisplay   — phase 1's AssetVisual (map/assetVisualRules), so the control room's
//                         placement, `hasOverlay` and the chips read it unchanged;
//   * compiledOf        — a rule set compiled once (memoised on the array);
//   * map marks         — feature properties and layers for the ring, the pulse and the badge
//                         around the asset marker (the marker's shape and pictogram still back the
//                         colour), and the pulse's animation (steady when the viewer prefers
//                         reduced motion). The map draws the strongest ring, the strongest pulse
//                         and the first badge — the badge as a dot with its words' initial, so two
//                         badges never differ by colour alone — and a "+" in that corner when the
//                         truck has more marks than drawn (every mark is listed, with its words, in
//                         the vehicle's card, the rail and the detail panel);
//   * markColour        — the colour of a mark (its token's marker colour; a badge without a
//                         token is slate).
// Colour tokens are phase 1's palette (map/assetStatus: marker colour + light / dark surfaces).

import type { FilterSpecification, Map as MaplibreMap } from 'maplibre-gl';
import { ASSET_STATUS, type AssetStatusKey } from './assetStatus';
import type { AssetOverlay, AssetVisual } from './assetVisualRules';
import type { AssetCircleLayer, AssetSymbolLayer } from './assetMarkerImages';
import {
  compileRules,
  compiledDefaults,
  evaluateDisplay,
  factsOfMotion,
  isHollowDisplay,
  type AssetDisplay,
  type CompiledRules,
  type DisplayOverlay,
  type DisplayRule,
} from './displayRules';

/** Phase 1's visual of a display (the control room's placement and chips read this shape). */
export function visualOfDisplay(d: AssetDisplay): AssetVisual {
  const overlays: AssetOverlay[] = d.overlays.map((o) => ({
    ruleId: o.rule_id,
    ...(o.mark === 'dashed' ? { shape: 'dashed' as const } : {}),
    ...(o.mark === 'badge' ? { badge: o.label || '' } : {}),
    ...((o.mark === 'ring' || o.mark === 'pulse') && o.token ? { ring: o.token as AssetStatusKey } : {}),
  }));
  return {
    key: d.base.token as AssetStatusKey,
    label: d.base.label,
    ruleId: d.base.rule_id,
    hollow: isHollowDisplay(d),
    overlays,
  };
}

const compiled = new WeakMap<readonly DisplayRule[], CompiledRules>();
/** A rule set compiled once (memoised on the array); none: the code defaults. */
export function compiledOf(rules: readonly DisplayRule[] | null | undefined, version = 0): CompiledRules {
  if (!rules) return compiledDefaults();
  let c = compiled.get(rules);
  if (!c || c.version !== version) {
    c = compileRules(rules, version);
    compiled.set(rules, c);
  }
  return c;
}

/** A tracker LexiStar does not know, by its motion, under the organisation's rules. */
export const displayOfMotion = (
  rules: CompiledRules,
  motion: string | null | undefined,
  speedKmh: number | null = null
): AssetDisplay => evaluateDisplay(rules, factsOfMotion(motion, speedKmh));

/** Slate-700: a badge without a colour of its own. */
export const NEUTRAL_MARK = '#334155';

/** A mark's colour on the map (its token's marker colour). */
export const markColour = (o: Pick<DisplayOverlay, 'token'>): string =>
  o.token ? ASSET_STATUS[o.token as AssetStatusKey].marker : NEUTRAL_MARK;

/** The marks a surface draws beside / around the marker (the dashed outline is the marker's own). */
export const extraMarks = (overlays: readonly DisplayOverlay[] | null | undefined): DisplayOverlay[] =>
  (overlays || []).filter((o) => o.mark !== 'dashed');

// ── Marks on a maplibre map ─────────────────────────────────────────────────────────────────

/** What a vehicle feature carries for the mark layers ('' = no such mark). */
export interface DisplayMarkFeatureProps {
  dsRing: string;
  dsPulse: string;
  /** The badge's colour ('' = no badge). */
  dsBadge: string;
  /** The corner image: the badge's initial (and "+" for marks not drawn), '' = none. */
  dsBadgeImg: string;
}

export const NO_MARKS: DisplayMarkFeatureProps = { dsRing: '', dsPulse: '', dsBadge: '', dsBadgeImg: '' };

/** The initial a badge shows on the map (its words' first letter or digit; else "!"). */
export const badgeInitial = (o: Pick<DisplayOverlay, 'label'>): string => {
  const m = /[A-Za-z0-9]/.exec(o.label || '');
  return m ? (m[0] as string).toUpperCase() : '!';
};

export const DISPLAY_BADGE_IMAGE_PREFIX = 'display-badge/';
/** The corner image's name: colour (rrggbb) and text (an initial, "+", or both). */
export const displayBadgeImageId = (hex: string, text: string): string =>
  `${DISPLAY_BADGE_IMAGE_PREFIX}${hex.replace('#', '').toLowerCase()}/${encodeURIComponent(text)}`;

/**
 * What the map draws of a display's marks: the strongest ring, the strongest pulse and the first
 * badge (overlays come highest priority first); the corner shows the badge's initial, and a "+"
 * when the truck has more marks than these (none of them is silently lost: the card lists all).
 */
export function displayMarkFeatureProps(
  overlays: readonly DisplayOverlay[] | null | undefined
): DisplayMarkFeatureProps {
  const marks = extraMarks(overlays);
  const first = (mark: DisplayOverlay['mark']) => marks.find((o) => o.mark === mark);
  const ring = first('ring');
  const pulse = first('pulse');
  const badge = first('badge');
  const drawn = [ring, pulse, badge].filter(Boolean).length;
  const more = marks.length > drawn;
  const badgeColour = badge ? markColour(badge) : more ? NEUTRAL_MARK : '';
  const text = `${badge ? badgeInitial(badge) : ''}${more ? '+' : ''}`;
  return {
    dsRing: ring ? markColour(ring) : '',
    dsPulse: pulse ? markColour(pulse) : '',
    dsBadge: badge ? markColour(badge) : '',
    dsBadgeImg: text ? displayBadgeImageId(badgeColour, text) : '',
  };
}

/** The SVG of a corner image (a disc, or a pill for two characters, with white text and keyline). */
export function displayBadgeSvg(hex: string, text: string, px: number): string {
  const w = text.length > 1 ? Math.round(px * 1.5) : px;
  const fs = Math.round(px * 0.62);
  const safe = text.replace(/[^A-Z0-9!+]/g, '');
  return (
    `<svg xmlns="http://www.w3.org/2000/svg" width="${w}" height="${px}" viewBox="0 0 ${w} ${px}">` +
    `<rect x="1" y="1" width="${w - 2}" height="${px - 2}" rx="${(px - 2) / 2}" fill="#${hex}" stroke="#ffffff" stroke-width="2"/>` +
    `<text x="${w / 2}" y="${px / 2}" dy="0.36em" text-anchor="middle" font-family="system-ui,-apple-system,Segoe UI,Roboto,sans-serif" font-size="${fs}" font-weight="700" fill="#ffffff">${safe}</text>` +
    `</svg>`
  );
}

const BADGE_PX = 16;
const BADGE_RATIO = 2;
const badgeImages = new WeakSet<MaplibreMap>();

/** Draw the corner images (`display-badge/<hex>/<text>`) on demand on this map (idempotent). */
export function installDisplayMarkImages(map: MaplibreMap): void {
  if (badgeImages.has(map)) return;
  badgeImages.add(map);
  map.on('styleimagemissing', (e: { id: string }) => {
    const m = /^display-badge\/([0-9a-f]{6})\/(.+)$/.exec(e.id);
    if (!m || typeof document === 'undefined') return;
    const hex = m[1] as string;
    const text = decodeURIComponent(m[2] as string).slice(0, 2);
    const px = BADGE_PX * BADGE_RATIO;
    const w = text.length > 1 ? Math.round(px * 1.5) : px;
    try {
      if (!map.hasImage(e.id)) map.addImage(e.id, { width: w, height: px, data: new Uint8Array(w * px * 4) }, { pixelRatio: BADGE_RATIO });
    } catch {
      return;
    }
    const img = new Image(w, px);
    img.onload = () => {
      try {
        if (map.hasImage(e.id)) map.updateImage(e.id, img);
      } catch {
        /* map disposed, or the style swapped meanwhile */
      }
    };
    img.src = `data:image/svg+xml;charset=utf-8,${encodeURIComponent(displayBadgeSvg(hex, text, px))}`;
  });
}

const has = (prop: keyof DisplayMarkFeatureProps): FilterSpecification => ['!=', ['get', prop], ''];
const andFilter = (a: FilterSpecification | undefined, b: FilterSpecification): FilterSpecification =>
  a ? (['all', a, b] as FilterSpecification) : b;

/** The pulse's ring layer ids of a prefix (animatePulse drives them). */
export const pulseLayerId = (prefix: string): string => `${prefix}-ds-pulse`;

/**
 * The mark layers for a source whose features carry displayMarkFeatureProps: `under` (ring and
 * pulse, below the marker's shadow) and `over` (the badge dot at the marker's top right, above
 * its face). `size` is the marker box in CSS px.
 */
export function displayMarkLayers(
  prefix: string,
  { size, filter, visible = true }: { size: number; filter?: FilterSpecification; visible?: boolean }
): { under: AssetCircleLayer[]; over: AssetSymbolLayer[] } {
  const layout = { visibility: visible ? ('visible' as const) : ('none' as const) };
  const r = Math.round(size * 0.5);
  return {
    under: [
      {
        id: pulseLayerId(prefix),
        type: 'circle',
        filter: andFilter(filter, has('dsPulse')),
        layout,
        paint: {
          'circle-radius': r + 2,
          'circle-color': ['to-color', ['get', 'dsPulse']],
          'circle-opacity': 0.18,
          'circle-stroke-color': ['to-color', ['get', 'dsPulse']],
          'circle-stroke-width': 3,
          'circle-stroke-opacity': 0.85,
        },
      },
      {
        id: `${prefix}-ds-ring`,
        type: 'circle',
        filter: andFilter(filter, has('dsRing')),
        layout,
        paint: {
          'circle-radius': r,
          'circle-color': 'rgba(0,0,0,0)',
          'circle-stroke-color': ['to-color', ['get', 'dsRing']],
          'circle-stroke-width': 3.5,
        },
      },
    ],
    over: [
      {
        id: `${prefix}-ds-badge`,
        type: 'symbol',
        filter: andFilter(filter, has('dsBadgeImg')),
        layout: {
          ...layout,
          'icon-image': ['get', 'dsBadgeImg'],
          'icon-allow-overlap': true,
          'icon-ignore-placement': true,
          'icon-rotation-alignment': 'viewport',
        },
        paint: {
          'icon-translate': [Math.round(size * 0.36), -Math.round(size * 0.36)],
          'icon-translate-anchor': 'viewport',
        },
      },
    ],
  };
}

/** The viewer prefers reduced motion (no animation then). */
export const prefersReducedMotion = (): boolean => {
  try {
    return typeof window !== 'undefined' && !!window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
  } catch {
    return false;
  }
};

/**
 * Animate the pulse layers (a growing, fading ring, 1.6 s per beat). Returns the stop function.
 * With reduced motion the ring stays steady. A missing layer (mid style swap) is skipped.
 */
export function animatePulse(map: MaplibreMap, layerIds: readonly string[], size: number): () => void {
  if (prefersReducedMotion() || typeof window === 'undefined') return () => undefined;
  const r = Math.round(size * 0.5);
  const start = Date.now();
  const id = window.setInterval(() => {
    const phase = ((Date.now() - start) % 1600) / 1600;
    for (const layer of layerIds) {
      try {
        if (!map.getLayer(layer)) continue;
        map.setPaintProperty(layer, 'circle-radius', r + 2 + phase * 10);
        map.setPaintProperty(layer, 'circle-stroke-opacity', 0.9 * (1 - phase));
        map.setPaintProperty(layer, 'circle-opacity', 0.25 * (1 - phase));
      } catch {
        /* map disposed */
      }
    }
  }, 80);
  return () => window.clearInterval(id);
}
